<script lang="ts">
  import moment from 'moment';
  import { onDestroy, onMount } from 'svelte';
  import {
    Chart as ChartJS,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    BarController,
  } from 'chart.js';
  import { Chart } from 'svelte-chartjs';
  import zoomPlugin from 'chartjs-plugin-zoom';
  import loader from 'src/components/cabinet/#assets/loader.svg';
  import { getTransactionsAnalytics, getLootPaidAnalytics } from 'src/components/cabinet/#services/transactionsAnalytics';
  import Table from '../Table.svelte';
  import { options } from '../../configs/BarOptions';
  import { getRewardsAnalytics } from '../../../#services/rewardsAnalytics';
  import { branchesDifference } from '../../utils/branches';
  import { getMathRewardsAnalytics } from '../../../#services/mathRewardsAnalytics';
  import { processChartDataset, resetChart } from '../../utils/common';
  import { requestsParams } from '../../configs/Requests';
  import DateRange from '../common/DateRange.svelte';
  import GameSelect from '../common/GameSelect.svelte';
  import {getProfile} from 'src/components/cabinet/#services/getUserProfile'
  import {dateFormat} from 'src/global/dateFormat'

  let chart;

  let purchasesBranch, analyticsBranch;

  let creator = 'all';
  let userId = null;

  let labels = [];
  let datasets = [];

  let data = { labels, datasets };

  let startDate = 'startDate'
  let endDate = 'endDate'

  const minDate = '2022-12-30';
  const maxDate = moment().format('YYYY-MM-DD');

  ChartJS.register(
    LinearScale,
    CategoryScale,
    BarElement,
    PointElement,
    LineElement,
    Legend,
    Tooltip,
    LineController,
    BarController,
    zoomPlugin,
  );

  function refreshLis() {
    const requestData = requestsParams.lootboxes.hard({
      creator: creator !== 'all' ? creator : undefined,
      userId: userId || undefined,
      startDate: startDate && startDate !== 'startDate' ? startDate : minDate,
      endDate: endDate && endDate !== 'endDate' ? endDate : maxDate,
    });

    getTransactionsAnalytics.send({ data: requestData });
  }

  function refreshAnalyticsBranch() {
    const requestData = requestsParams.lootboxes.rewards({
      game: creator !== 'all' ? creator : undefined,
      userId: userId || undefined,
      startDate: startDate && startDate !== 'startDate' ? startDate : minDate,
      endDate: endDate && endDate !== 'endDate' ? endDate : maxDate,
    });

    getMathRewardsAnalytics.send({ data: requestData });
  }

  function refreshLootPaidAnalytics() {
    getLootPaidAnalytics.send({ data: {
        game: creator !== 'all' ? creator : undefined,
        userId: userId || undefined,
        startDate: startDate && startDate !== 'startDate' ? startDate : minDate,
        endDate: endDate && endDate !== 'endDate' ? endDate : maxDate,
    }});
  }

  function refresh() {
    resetChart(chart, labels, datasets);
    refreshLis();
    refreshAnalyticsBranch();
    refreshLootPaidAnalytics();
  }

  getTransactionsAnalytics.subscribe((value) => {
    if (value.hasValue && !value.error && !value.loading) {
      processChartDataset(value.value, labels, datasets, true);
      try {
        purchasesBranch = value.value.branches[0]?.values;
      } catch (e) {}
    }
  });

  getMathRewardsAnalytics.subscribe((value) => {
    if (value.hasValue && !value.error && !value.loading) {
      processChartDataset(value.value, labels, datasets, false, 1);
      try {
        analyticsBranch = value?.value?.branches[0].values;
      } catch (e) {}
    }
  });

  onDestroy(() => {
    resetChart(chart);
    getTransactionsAnalytics.reset();
    getMathRewardsAnalytics.reset();
  });

  onMount(() => {
    refresh();
  });
</script>

<div class="buttons">
  <DateRange bind:startDate={startDate} bind:endDate={endDate} {minDate} {maxDate} refreshCallback={() => refresh()} />
</div>

<hr>

<div class="buttons">
  <div class="pc-line">
    <span class="date-label">Game</span>
    <GameSelect bind:creator={creator} refreshCallback={() => refresh()} />
  </div>
  <div class="pc-line">
    <span class="date-label">User</span>
    <input type="text" class="form-control" bind:value={userId} placeholder="Input UserID" on:input={refresh}>
  </div>
</div>
<hr>
<div class="buttons">
  <div class="pc-line">
    <button class="btn btn-primary btn-filter" on:click={refresh}>Refresh</button>
  </div>
</div>
<p class="description">Summarized purchases and rewards from lootboxes in LIS.</p>
<div class="graphic">
  {#if $getTransactionsAnalytics.error || $getRewardsAnalytics.error || $getMathRewardsAnalytics.error}
    Something went wrong. Try refreshing the page.
  {:else if $getTransactionsAnalytics.loading || $getRewardsAnalytics.loading || $getMathRewardsAnalytics.loading}
    <div class="graphic-loading">
      <img src="{loader}" alt="">
    </div>
  {:else if $getTransactionsAnalytics.value && $getMathRewardsAnalytics.value}
    <Chart bind:this={chart} type="bar" {data} width={500} height={270} {options} />
    <Table branches={{
      'Spent, LIS': purchasesBranch,
      '📈 Rewarded, LIS': analyticsBranch.map(v => ({ ...v, amount: v.amount * -1 })),
      'Saldo, LIS': branchesDifference(
        purchasesBranch,
        analyticsBranch,
      ),
    }} isTableHidden={false} />
  {/if}
</div>
<br>
{#if !$getLootPaidAnalytics.hasValue && $getLootPaidAnalytics.loading}
  <div class="graphic-loading">
    <img src="{loader}" alt="">
  </div>
{:else if !$getLootPaidAnalytics.loading && $getLootPaidAnalytics.error}
  Something went wrong. Try refreshing the page.
{:else if $getLootPaidAnalytics.hasValue}
  <table class="table">
    <thead>
    <tr>
      <th>Date</th>
      <th>Wooden, LIS</th>
      <th>Gold, LIS</th>
      <th>Iron, LIS</th>
      <th>Diamond, LIS</th>
    </tr>
    </thead>
    <tbody>
    {#if $getLootPaidAnalytics.value?.total}
      <tr>
        <td><strong>Total</strong></td>
        <td>{$getLootPaidAnalytics.value?.total?.wooden | 0}</td>
        <td>{$getLootPaidAnalytics.value?.total?.gold | 0}</td>
        <td>{$getLootPaidAnalytics.value?.total?.iron | 0}</td>
        <td>{$getLootPaidAnalytics.value?.total?.diamond | 0}</td>
      </tr>
    {/if}
    {#each $getLootPaidAnalytics.value?.data as {date, wooden = 0, gold = 0, iron = 0, diamond = 0}}
      <tr>
        <td>{dateFormat(date, 'month')}</td>
        <td>{wooden}</td>
        <td>{gold}</td>
        <td>{iron}</td>
        <td>{diamond}</td>
      </tr>
    {/each}
    </tbody>
  </table>
  <i>How much LIS did we actually give out to players from lootboxes</i>
{/if}

<style>
  .table th {
    background-color: #e9ecef;
  }

  .table th,
  .table td {
    padding:  8px 15px;
  }
</style>
